import { ArrowUpRight, Ban, CheckCheck, Inbox, Send, RotateCcw, Settings, Truck } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { AuditTrailList } from '../../../../components/index-page/AuditTrailList';
import { Menu } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner, type ProfileBannerTone } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { personsApi } from '../../../human-resources/personnel/personnelApi';
import { DispatchedDocumentModal } from '../components/DispatchedDocumentModal';
import { ReceivedDocumentModal } from '../components/ReceivedDocumentModal';
import { RouteDocumentModal } from '../components/RouteDocumentModal';
import { RoutingHistoryPanel } from '../components/RoutingHistoryPanel';
import { RoutingStatusPill } from '../components/RoutingStatusPill';
import { receivedDocumentsApi } from '../routingApi';
import { DISPATCHING_PATH, RECEIVING_PATH } from '../routingRules';
import { ROUTING_STATUS, type ReceivedDocumentDetail } from '../routingTypes';

type TabKey = 'info' | 'routing' | 'audit';

/**
 * One received hard copy — what it is, who sent it, every desk it went to, and every write ever
 * made to it.
 *
 * **On the header layout, three tabs, landing on Info** — a desk opens a received document to see
 * where it is and who has it. There is no Preview: a hard copy is paper, and a file between units
 * travels as a dispatch to an Inbox. **Audit trail** carries `audit-logs.read` — a tab and its
 * panel share one gate, and a gated tab is HIDDEN.
 *
 * **The banner states where it stands, for every state, and owns its next act**: Route while it is
 * on hand, Acknowledge while it is in transit (a write outside a modal, so it confirms), the
 * dispatch while it is out. Dispatching it is the header menu's — a record-level control beside
 * the status, under Dispatching's own key.
 */
export function ReceivedDocumentProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canReadPeople = usePermission('personnel.read');

  const [document, setDocument] = useState<ReceivedDocumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('info');
  const [editing, setEditing] = useState(false);
  const [routing, setRouting] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [running, setRunning] = useState(false);
  const [panelKey, setPanelKey] = useState(0);

  /*
    THE SENDER'S NAME, in its own call and only where there is one to resolve — the identity split.
    A failure leaves it unresolved and the page standing.
  */
  const [senderName, setSenderName] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      receivedDocumentsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setDocument(next);
          setError(null);

          // `== null`, never `===`: a null property is omitted from the wire and arrives undefined.
          if (next.senderPersonId == null || !canReadPeople) {
            setSenderName(null);
            return;
          }

          personsApi
            .resolve([next.senderPersonId], signal)
            .then((people) => {
              if (!signal?.aborted) setSenderName(people[0]?.displayName ?? null);
            })
            .catch(() => setSenderName(null));
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this document.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id, canReadPeople],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /** A write changed the route — the header, banner and counts move with it. */
  const changed = useCallback(() => {
    setPanelKey((k) => k + 1);
    load();
  }, [load]);

  async function restore() {
    if (!document) return;

    setRunning(true);

    try {
      await receivedDocumentsApi.restore(document.id);
      toast.success(`Restored ${document.trackingNumber}.`);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this document.', 0));
    } finally {
      setRunning(false);
    }
  }

  // The open routing THIS reader may take in — the server says which, per line.
  const mine = document?.pendingRoutes.find((p) => p.canAcknowledge) ?? null;

  async function acknowledge() {
    if (!document || !mine) return;

    setRunning(true);

    try {
      await receivedDocumentsApi.acknowledge(document.id, mine.id);
      toast.success(`Acknowledged at ${mine.toOrgUnitName ?? 'the unit'}.`);
      setAcknowledging(false);
      changed();
    } catch (caught) {
      setAcknowledging(false);
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not acknowledge it.');
    } finally {
      setRunning(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Document Management',
    'Routing',
    { label: 'Receiving', to: RECEIVING_PATH },
    document?.trackingNumber ?? 'Document',
  ];

  // The full-page error is for a failure with NOTHING to show; a failed refresh keeps the page.
  if (error && !document) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (loading && !document) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  if (!document) return null;

  const blocked = document.canRoute ? null : (document.routeBlockedReason ?? 'It cannot be routed on now.');

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell<TabKey>
        id="received-document-profile"
        panel={tab === 'info' ? 'cards' : 'bleed'}
        header={
          <ProfileHeader
            id="received-document-profile-header"
            icon={<Inbox className="h-6 w-6" aria-hidden="true" />}
            title={document.subject}
            meta={[document.trackingNumber, document.documentTypeName, `From ${document.senderOrgUnitName ?? document.senderOffice ?? '—'}`]}
            status={<RoutingStatusPill status={document.status} label={document.statusLabel} isActive={document.isActive} />}
            createdBy={document.createdByName}
            createdAt={document.createdAt}
            actions={
              <Menu
                id="received-document-profile-cog"
                items={[
                  {
                    key: 'dispatch',
                    label: 'Dispatch',
                    icon: <Truck className="h-4 w-4" aria-hidden="true" />,
                    permission: 'document-dispatching.create',
                    disabledReason: blocked,
                    onClick: () => setDispatching(true),
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Document options"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={banner()}
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'routing',
            label: (
              <span className="inline-flex items-center gap-1.5">
                Routing <CountBadge count={document.routeCount} />
              </span>
            ),
          },
          { key: 'audit', label: 'Audit trail', permission: 'audit-logs.read' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="received-document-profile-details"
              title="Details"
              onEdit={() => setEditing(true)}
              editPermission="document-receiving.update"
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Tracking no.', value: document.trackingNumber, mono: true },
                  { label: 'Subject', value: document.subject },
                  { label: 'Document type', value: document.documentTypeName },
                  // Both rows, always — one is a dash, which says which side of the organization sent it.
                  { label: 'From (unit)', value: document.senderOrgUnitName },
                  { label: 'From (organization)', value: document.senderOffice },
                  {
                    /*
                      `Hidden` is NOT an em dash. A reader without `personnel.read` is being told the
                      document HAS a named sender and this page has not been given the name. A unit
                      sender never has one.
                    */
                    label: 'Named sender',
                    value: document.senderPersonId == null ? null : canReadPeople ? senderName : 'Hidden',
                  },
                  { label: "Sender's reference no.", value: document.senderReferenceNumber, mono: true },
                  {
                    label: 'Date on the document',
                    value: document.documentDate != null ? formatDateOnly(document.documentDate) : null,
                    mono: true,
                  },
                  { label: 'Received', value: formatDateTime(document.receivedAt), mono: true },
                  { label: 'Receiving unit', value: document.receivingOrgUnitName },
                  // A SET — several units once it has been routed to more than one; one line, joined.
                  { label: 'With', value: document.holders.length > 0 ? document.holders.map((h) => h.name).join(', ') : null },
                  { label: 'Remarks', value: document.remarks },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="received-document-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: document.createdByName },
                  { label: 'Created', value: formatDateTime(document.createdAt), mono: true },
                  { label: 'Updated by', value: document.updatedByName },
                  { label: 'Updated', value: document.updatedAt ? formatDateTime(document.updatedAt) : null, mono: true },
                ]}
              />
            </ProfileCard>
          </>
        ) : tab === 'routing' ? (
          <div className="flex h-full min-h-0 flex-col p-4">
            <RoutingHistoryPanel
              key={panelKey}
              recordId={document.id}
              api={receivedDocumentsApi}
              movement={{
                api: receivedDocumentsApi,
                modulePrefix: 'document-receiving',
                // Receiving's word for routing — product call, 10 Oct 2026.
                verb: 'Dispatch',
                onRoute: () => setRouting(true),
                routeDisabledReason: blocked,
              }}
              onChanged={changed}
            />
          </div>
        ) : (
          <div className="flex h-full min-h-0 flex-col p-4">
            <AuditTrailList
              module="document-receiving"
              entityId={document.id}
              emptyMessage="No recorded activity for this document yet."
            />
          </div>
        )}
      </ProfileHeaderShell>

      {editing ? (
        <ReceivedDocumentModal
          open
          editing={document}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            changed();
          }}
        />
      ) : null}

      {routing ? (
        <RouteDocumentModal
          open
          api={receivedDocumentsApi}
          document={document}
          verb="Dispatch"
          onClose={() => setRouting(false)}
          onSaved={() => {
            setRouting(false);
            changed();
          }}
        />
      ) : null}

      {dispatching ? (
        <DispatchedDocumentModal
          open
          editing={null}
          initialReceivedId={document.id}
          onClose={() => setDispatching(false)}
          onSaved={(saved) => {
            setDispatching(false);
            navigate(`${DISPATCHING_PATH}/${saved.id}`);
          }}
        />
      ) : null}

      {acknowledging && mine ? (
        <ConfirmDialog
          open
          onClose={() => setAcknowledging(false)}
          onConfirm={() => void acknowledge()}
          title="Acknowledge receipt"
          requireConfirm
          loading={running}
          id="received-document-acknowledge-dialog"
          body={
            <p>
              {mine.toOrgUnitName ?? 'The unit'} takes {document.trackingNumber} in. It is that unit&apos;s
              from now on, and the forward becomes part of its history — it can no longer be cancelled.
            </p>
          }
        />
      ) : null}
    </div>
  );

  /** The banner, one per state — it renders for every state, not only the bad ones. */
  function banner(): ReactNode {
    if (!document) return null;

    if (error) {
      return (
        <ProfileBanner
          id="received-document-profile-banner"
          tone="red"
          icon={<Ban className="h-4 w-4" aria-hidden="true" />}
          message={error.message}
        />
      );
    }

    if (!document.isActive) {
      return (
        <ProfileBanner
          id="received-document-profile-banner"
          tone="red"
          icon={<Ban className="h-4 w-4" aria-hidden="true" />}
          message="This document is deleted. It is off the register; its route is kept."
          action={
            <PermissionGate permission="document-receiving.delete">
              <Button
                id="received-document-profile-restore"
                variant="secondary"
                leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                onClick={() => void restore()}
                disabled={running}
                loading={running}
                loadingLabel="Restoring"
              >
                Restore
              </Button>
            </PermissionGate>
          }
        />
      );
    }

    const state = stateOf(document);

    return (
      <ProfileBanner
        id="received-document-profile-banner"
        tone={state.tone}
        icon={state.icon}
        message={state.message}
        action={
          // The RECIPIENT's act first — a reader the server lets take an open routing in.
          mine ? (
            // WRITES, outside a modal — so it confirms with a tick.
            <PermissionGate permission="document-receiving.route">
              <Button
                id="received-document-profile-acknowledge"
                variant="primary"
                leading={<CheckCheck className="h-4 w-4" aria-hidden="true" />}
                onClick={() => setAcknowledging(true)}
              >
                Acknowledge
              </Button>
            </PermissionGate>
          ) : document.canRoute ? (
            // Opens a form, so no confirmation of its own — the form's tick is the gate.
            <PermissionGate permission="document-receiving.route">
              <Button
                id="received-document-profile-route"
                variant="primary"
                leading={<Send className="h-4 w-4" aria-hidden="true" />}
                onClick={() => setRouting(true)}
              >
                Dispatch
              </Button>
            </PermissionGate>
          ) : document.dispatchedDocumentId != null ? (
            <PermissionGate permission="document-dispatching.read">
              <Button
                id="received-document-profile-open-dispatch"
                variant="secondary"
                leading={<ArrowUpRight className="h-4 w-4" aria-hidden="true" />}
                onClick={() => navigate(`${DISPATCHING_PATH}/${document.dispatchedDocumentId}`)}
              >
                Open {document.dispatchedTrackingNumber}
              </Button>
            </PermissionGate>
          ) : null
        }
      />
    );
  }
}

function stateOf(document: ReceivedDocumentDetail): { tone: ProfileBannerTone; icon: ReactNode; message: string } {
  switch (document.status) {
    case ROUTING_STATUS.inTransit: {
      const to = document.pendingRoutes.map((p) => p.toOrgUnitName ?? 'a unit');
      const holders = document.holders.map((h) => h.name).join(', ');

      // Every open routing named — there may be several, and each unit acknowledges its own.
      return {
        tone: 'amber',
        icon: <Send className="h-4 w-4" aria-hidden="true" />,
        message:
          to.length > 0
            ? `On its way to ${to.join(', ')} — not yet acknowledged. With ${holders || 'the unit that sent it'} until then.`
            : 'In transit to another unit, awaiting acknowledgement.',
      };
    }

    case ROUTING_STATUS.dispatched:
      return {
        tone: 'slate',
        icon: <Truck className="h-4 w-4" aria-hidden="true" />,
        // A dispatch to a unit carries on in that unit's Inbox — on the DISPATCH's route, not this one.
        message: document.dispatchedTo
          ? `Dispatched to ${document.dispatchedTo} as ${document.dispatchedTrackingNumber}. This document's route ends here; the dispatch carries it on.`
          : "Dispatched. This document's route ends here.",
      };

    default:
      return {
        tone: 'emerald',
        icon: <Inbox className="h-4 w-4" aria-hidden="true" />,
        // The next act is offered only to a reader who may do it; anyone else is told why not.
        message: `With ${document.holders.map((h) => h.name).join(', ')}. ${
          document.canRoute
            ? 'Dispatch it to the next unit from the Routing tab, or send it out from the menu once it is done.'
            : (document.routeBlockedReason ?? '')
        }`,
      };
  }
}
